import {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ClipboardEvent,
} from "react";
import styles from "./SettingsAccountPage.module.css";
import { useSettingsProfileViewModel } from "@/features/settings/viewModal/useSettingsProfileViewModel";
import settingsHouseImg from "@/assets/images/settings-house.png";
import { toastService } from "@/shared/toast";
import { sendOtpApi, verifyOtpApi, updateUserProfileApi } from "@/features/auth/model/authApi";
import { updateProfile } from "@/features/settings/services/profile.adapter";
import {
  PHONE_DIAL_CODE_OPTIONS,
  type PhoneDialCode,
} from "@/features/auth/constants/phoneDialCodes";

// ============================================================================
// Custom Icons Matching Mockup
// ============================================================================

const PencilEditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const UserOutlineIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailOutlineIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const PhoneOutlineIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LocationPinOutlineIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const SettingsAccountPage = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    form: { profile, isEdit },
    ui,
    actions,
  } = useSettingsProfileViewModel();

  // Inline Mobile Number Update & OTP Verification State
  const [phoneEditMode, setPhoneEditMode] = useState<"idle" | "input" | "otp">("idle");
  const [dialCode, setDialCode] = useState<PhoneDialCode>(
    PHONE_DIAL_CODE_OPTIONS[0]?.dialCode ?? "+1"
  );
  const [nationalNumber, setNationalNumber] = useState("");
  const [otpDigits, setOtpDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const fullPhone = useMemo(() => {
    const cleaned = nationalNumber.replace(/\D/g, "");
    return `${dialCode}${cleaned}`;
  }, [dialCode, nationalNumber]);

  const handleStartPhoneUpdate = () => {
    setPhoneEditMode("input");
    setPhoneError("");
    setOtpDigits(["", "", "", "", "", ""]);
    setNationalNumber("");
  };

  const handleCancelPhoneUpdate = () => {
    if (isSendingOtp || isVerifyingOtp) return;
    setPhoneEditMode("idle");
    setPhoneError("");
    setOtpDigits(["", "", "", "", "", ""]);
    setNationalNumber("");
  };

  const handleSendOtp = async () => {
    const cleaned = nationalNumber.replace(/\D/g, "");
    if (!cleaned) {
      setPhoneError("Please enter a mobile number.");
      return;
    }
    if (cleaned.length !== 10) {
      setPhoneError("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      setIsSendingOtp(true);
      setPhoneError("");
      const response = await sendOtpApi({ phone: fullPhone });
      if (response.status === "success" || response.status === "ok") {
        setPhoneEditMode("otp");
        setCountdown(30);
        setOtpDigits(["", "", "", "", "", ""]);
        toastService.show(`Verification code sent to ${fullPhone}`, "success");
        setTimeout(() => {
          document.getElementById("inline-phone-otp-0")?.focus();
        }, 100);
      } else {
        setPhoneError(response.message || "Failed to send verification code.");
      }
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setPhoneError(errorObj?.response?.data?.message || "Failed to send code. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0 || isSendingOtp) return;
    try {
      setIsSendingOtp(true);
      setPhoneError("");
      await sendOtpApi({ phone: fullPhone });
      setCountdown(30);
      toastService.show(`Verification code resent to ${fullPhone}`, "success");
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setPhoneError(
        errorObj?.response?.data?.message || "Failed to resend code. Please try again."
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpChange = (val: string, idx: number) => {
    const clean = val.replace(/\D/g, "").slice(-1);
    const updated = [...otpDigits];
    updated[idx] = clean;
    setOtpDigits(updated);
    setPhoneError("");

    if (clean && idx < 5) {
      document.getElementById(`inline-phone-otp-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e: KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
      document.getElementById(`inline-phone-otp-${idx - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const updated = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      updated[i] = pasted[i] || "";
    }
    setOtpDigits(updated);
    setPhoneError("");
    const targetIdx = Math.min(pasted.length, 5);
    document.getElementById(`inline-phone-otp-${targetIdx}`)?.focus();
  };

  const handleVerifyAndUpdatePhone = async () => {
    const fullOtp = otpDigits.join("");
    if (fullOtp.length < 6) {
      setPhoneError("Please enter all 6 digits of the verification code.");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      setPhoneError("");

      const verifyRes = await verifyOtpApi({ phone: fullPhone, otp: fullOtp });
      if (verifyRes.status !== "success") {
        setPhoneError(verifyRes.message || "Invalid verification code. Please try again.");
        setIsVerifyingOtp(false);
        return;
      }

      // Verified! Update user profile phone number
      try {
        const updateRes = await updateUserProfileApi({
          is_phone_verified: true,
          phone: fullPhone,
          force_mobile_update: false,
        });

        if (updateRes.status !== "success") {
          await updateProfile({
            first_name: profile.firstName,
            last_name: profile.lastName,
            phone: fullPhone,
            mailing_address: profile.address,
          });
        }
      } catch {
        await updateProfile({
          first_name: profile.firstName,
          last_name: profile.lastName,
          phone: fullPhone,
          mailing_address: profile.address,
        });
      }

      await actions.getUserProfile();
      toastService.show("Phone number updated successfully!", "success");
      handleCancelPhoneUpdate();
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setPhoneError(errorObj?.response?.data?.message || "Verification failed. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const displayName = useMemo(() => {
    const fullName = `${profile.firstName} ${profile.lastName}`.trim();
    return fullName || "User";
  }, [profile.firstName, profile.lastName]);

  const initials = useMemo(() => {
    const firstInitial = profile.firstName.trim().charAt(0);
    const lastInitial = profile.lastName.trim().charAt(0);
    return `${firstInitial}${lastInitial}`.toUpperCase() || "U";
  }, [profile.firstName, profile.lastName]);

  const onAvatarButtonClick = () => {
    if (!isEdit || ui.uploadingAvatar) {
      return;
    }
    fileInputRef.current?.click();
  };

  const onAvatarSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    void actions.uploadAvatar(file);
    event.target.value = "";
  };

  const onEditClick = () => {
    if (isEdit) {
      void actions.saveProfile();
      return;
    }
    actions.startEdit();
  };

  return (
    <div className={styles.accountPage}>
      {/* 1. Profile Header Banner */}
      <section className={styles.profileHeader}>
        <div
          className={styles.headerHouseBg}
          style={{ backgroundImage: `url(${settingsHouseImg})` }}
        />
        <div className={styles.headerOverlay} />

        <div className={styles.profileHeaderContent}>
          <div className={styles.profileHeaderLeft}>
            <button
              type="button"
              className={styles.avatarButton}
              onClick={onAvatarButtonClick}
              disabled={!isEdit || ui.uploadingAvatar}
              title={isEdit ? "Click to change avatar photo" : undefined}
              aria-label={isEdit ? "Change avatar" : "User avatar"}
            >
              {profile.avatarUrl ? (
                <img src={profile.avatarUrl} alt={displayName} className={styles.avatarImage} />
              ) : (
                <div className={styles.avatar}>{initials}</div>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={onAvatarSelected}
            />
            <div className={styles.nameBlock}>
              <h1 className={styles.name}>{displayName}</h1>
              <p className={styles.email} title={profile.email}>
                {profile.email || "-"}
              </p>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.editBtn}
              onClick={onEditClick}
              disabled={ui.loading || ui.uploadingAvatar}
            >
              <PencilEditIcon />
              <span>{isEdit ? "Save" : "Edit"}</span>
            </button>
          </div>
        </div>
      </section>

      {ui.errorMessage && (
        <section className={styles.errorBox} role="alert">
          {ui.errorMessage}
        </section>
      )}

      {/* 2. Main Profile Form Panel */}
      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.formTitle}>Profile</h2>
          <p className={styles.formSubtitle}>
            Update your personal information and keep your account details up to date.
          </p>
        </div>

        <div className={styles.formGrid}>
          {/* First Name */}
          <div className={styles.formItem}>
            <label className={styles.fieldLabel}>
              First name
              {isEdit && <span className={styles.requiredMark}> *</span>}
            </label>
            <div className={styles.fieldInputRow}>
              <div className={styles.fieldIconCircle} aria-hidden="true">
                <UserOutlineIcon />
              </div>
              <div className={styles.inputWrapper}>
                {isEdit ? (
                  <>
                    <input
                      value={profile.firstName}
                      onChange={(event) => actions.onFirstNameChange(event.target.value)}
                      className={`${styles.inputPill} ${ui.firstNameError ? styles.inputError : ""}`}
                      placeholder="First name"
                      required
                      aria-required="true"
                      aria-invalid={Boolean(ui.firstNameError)}
                      aria-describedby={ui.firstNameError ? "first-name-error" : undefined}
                    />
                    {ui.firstNameError && (
                      <span id="first-name-error" className={styles.fieldError} role="alert">
                        {ui.firstNameError}
                      </span>
                    )}
                  </>
                ) : (
                  <div className={styles.inputPillReadonly}>{profile.firstName || "-"}</div>
                )}
              </div>
            </div>
          </div>

          {/* Last Name */}
          <div className={styles.formItem}>
            <label className={styles.fieldLabel}>Last name</label>
            <div className={styles.fieldInputRow}>
              <div className={styles.fieldIconCircle} aria-hidden="true">
                <UserOutlineIcon />
              </div>
              <div className={styles.inputWrapper}>
                {isEdit ? (
                  <input
                    value={profile.lastName}
                    onChange={(event) => actions.onLastNameChange(event.target.value)}
                    className={styles.inputPill}
                    placeholder="Last name"
                  />
                ) : (
                  <div className={styles.inputPillReadonly}>{profile.lastName || "-"}</div>
                )}
              </div>
            </div>
          </div>

          {/* User Email */}
          <div className={styles.formItem}>
            <label className={styles.fieldLabel}>User email</label>
            <div className={styles.fieldInputRow}>
              <div className={styles.fieldIconCircle} aria-hidden="true">
                <MailOutlineIcon />
              </div>
              <div className={styles.inputWrapper}>
                <div className={styles.inputPillReadonly}>{profile.email || "-"}</div>
              </div>
            </div>
          </div>

          {/* Phone Number */}
          <div className={styles.formItem}>
            <label className={styles.fieldLabel}>Phone Number</label>
            <div className={styles.fieldInputRow}>
              <div className={styles.fieldIconCircle} aria-hidden="true">
                <PhoneOutlineIcon />
              </div>
              <div className={styles.inputWrapper}>
                {phoneEditMode === "idle" && (
                  <div className={styles.phonePillRow}>
                    <div className={styles.inputPillReadonly} style={{ flex: 1 }}>
                      {profile.phone || "-"}
                    </div>
                    <button
                      type="button"
                      className={styles.updatePhoneBtn}
                      onClick={handleStartPhoneUpdate}
                      title="Update phone number with OTP verification"
                    >
                      <PencilEditIcon />
                      <span>{profile.phone ? "Update" : "Add Number"}</span>
                    </button>
                  </div>
                )}

                {phoneEditMode === "input" && (
                  <div className={styles.inlinePhoneUpdateBox}>
                    <div className={styles.inlinePhoneInputRow}>
                      <select
                        className={styles.dialCodeSelect}
                        value={dialCode}
                        onChange={(e) => setDialCode(e.target.value as PhoneDialCode)}
                        disabled={isSendingOtp}
                        aria-label="Country dial code"
                      >
                        {PHONE_DIAL_CODE_OPTIONS.map((opt) => (
                          <option key={opt.dialCode} value={opt.dialCode}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      <input
                        type="tel"
                        className={styles.phoneInputInline}
                        placeholder="10-digit mobile number"
                        value={nationalNumber}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setNationalNumber(cleaned);
                          setPhoneError("");
                        }}
                        disabled={isSendingOtp}
                        maxLength={10}
                        autoFocus
                      />

                      <button
                        type="button"
                        className={styles.inlineSubmitBtn}
                        onClick={handleSendOtp}
                        disabled={isSendingOtp || nationalNumber.replace(/\D/g, "").length !== 10}
                      >
                        {isSendingOtp ? "Sending..." : "Send OTP"}
                      </button>

                      <button
                        type="button"
                        className={styles.inlineCancelBtn}
                        onClick={handleCancelPhoneUpdate}
                        disabled={isSendingOtp}
                      >
                        Cancel
                      </button>
                    </div>

                    {phoneError && <div className={styles.inlineErrorText}>{phoneError}</div>}
                  </div>
                )}

                {phoneEditMode === "otp" && (
                  <div className={styles.inlinePhoneUpdateBox}>
                    <div className={styles.inlineOtpHeader}>
                      <span className={styles.inlineOtpNotice}>
                        Enter 6-digit code sent to <strong>{fullPhone}</strong>
                      </span>
                      <button
                        type="button"
                        className={styles.inlineEditNumberLink}
                        onClick={() => {
                          setPhoneEditMode("input");
                          setPhoneError("");
                        }}
                        disabled={isVerifyingOtp}
                      >
                        Edit number
                      </button>
                    </div>

                    <div className={styles.inlineOtpInputRow}>
                      <div className={styles.inlineOtpBoxes}>
                        {otpDigits.map((digit, i) => (
                          <input
                            key={i}
                            id={`inline-phone-otp-${i}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={digit}
                            maxLength={1}
                            className={styles.otpDigitInput}
                            onChange={(e) => handleOtpChange(e.target.value, i)}
                            onKeyDown={(e) => handleOtpKeyDown(e, i)}
                            onPaste={handleOtpPaste}
                            disabled={isVerifyingOtp}
                            autoFocus={i === 0}
                          />
                        ))}
                      </div>

                      <button
                        type="button"
                        className={styles.inlineSubmitBtn}
                        onClick={handleVerifyAndUpdatePhone}
                        disabled={isVerifyingOtp || otpDigits.join("").length !== 6}
                      >
                        {isVerifyingOtp ? "Verifying..." : "Verify & Save"}
                      </button>

                      <button
                        type="button"
                        className={styles.inlineCancelBtn}
                        onClick={handleCancelPhoneUpdate}
                        disabled={isVerifyingOtp}
                      >
                        Cancel
                      </button>
                    </div>

                    <div className={styles.inlineResendRow}>
                      <span>Didn&apos;t receive code?</span>
                      <button
                        type="button"
                        className={styles.resendBtn}
                        onClick={handleResendOtp}
                        disabled={countdown > 0 || isSendingOtp || isVerifyingOtp}
                      >
                        {countdown > 0 ? `Resend in ${countdown}s` : "Resend Code"}
                      </button>
                    </div>

                    {phoneError && <div className={styles.inlineErrorText}>{phoneError}</div>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mailing Address (Full Width) */}
          <div className={`${styles.formItem} ${styles.formItemFull}`}>
            <label className={styles.fieldLabel}>Mailing Address</label>
            <div className={styles.fieldInputRow}>
              <div className={styles.fieldIconCircle} aria-hidden="true">
                <LocationPinOutlineIcon />
              </div>
              <div className={styles.inputWrapper}>
                {isEdit ? (
                  <input
                    value={profile.address}
                    disabled={true}
                    onChange={(event) => actions.onAddressChange(event.target.value)}
                    className={styles.inputPill}
                    placeholder="Mailing address"
                  />
                ) : (
                  <div className={styles.inputPillReadonly}>{profile.address || "-"}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className={styles.formFooterActions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={actions.cancelEdit}
            disabled={!isEdit}
          >
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
};
